import { LoadingContent, useAnimatedList, Spinner } from '../components/feedback';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ClientTemplateEditor } from './ClientTemplateEditor';
import { ClientTemplateVerifications } from '../components/ClientPackVerification';
import { addonKindsOf, clientTemplateFits, type ClientModTemplate } from '@mcpanel/core';
import { api, ApiError } from '../api/client';
import { clientModsApi } from '../api/client-mod-templates';
import { errorMessage, useApp } from '../app-context';
import { Dialog, Empty, ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';

const versionLabel = (template: ClientModTemplate) => template.mcVersion ?? '指定なし';

/** まとめて割り当てる。使えるのは Mod なしで版が合うサーバーだけ。1 台だけ選ぶのもここか、サーバーの「ランチャーに公開する」で */
function AssignDialog({ template, close, done }: { template: ClientModTemplate; close: () => void; done: () => void }) {
  const { notify } = useApp();
  const servers = usePoll(() => api.servers(), 60000);
  const [chosen, setChosen] = useState(() => new Set((template.servers ?? []).map((server) => server.id)));
  const [defaultForNew, setDefaultForNew] = useState(Boolean(template.defaultForNew));
  const [busy, setBusy] = useState(false);
  const eligible = (servers.data ?? []).filter(
    (server) => !addonKindsOf(server.software.family).mods && clientTemplateFits(template, server.software.mcVersion),
  );
  const toggle = (id: string, on: boolean) =>
    setChosen((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const save = async () => {
    setBusy(true);
    try {
      await clientModsApi.assign(template.id, { serverIds: [...chosen], defaultForNew });
      notify(`「${template.name}」を ${chosen.size} 台のサーバーに割り当てました`);
      done();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={`「${template.name}」を割り当て`}
      onClose={close}
      actions={
        <>
          <button className="btn" onClick={close}>
            キャンセル
          </button>
          <button className="btn primary" disabled={busy || !servers.data} onClick={() => void save()}>
            {busy && <Spinner />}
            保存
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="small muted">
          チェックしたサーバーにこのテンプレートを選びます。外したサーバーは「なし」に戻ります。選べるのは Mod なしの
          {template.mcVersion ? ` ${template.mcVersion} の` : ''}サーバーだけです。
        </p>
        <ErrorAlert error={servers.error} />
        {servers.data && eligible.length === 0 && <Empty>このテンプレートを使えるサーバーはありません。</Empty>}
        {eligible.map((server) => (
          <label className="check" key={server.id}>
            <input
              type="checkbox"
              checked={chosen.has(server.id)}
              onChange={(event) => toggle(server.id, event.target.checked)}
            />
            {server.name}
            <span className="small muted">
              {server.software.family} {server.software.mcVersion}
            </span>
          </label>
        ))}
        <label className="check">
          <input
            type="checkbox"
            checked={defaultForNew}
            disabled={!template.mcVersion}
            onChange={(event) => setDefaultForNew(event.target.checked)}
          />
          この版（{template.mcVersion ?? '版の指定なし'}）で新しく作るサーバーに、最初から選んでおく
        </label>
        {!template.mcVersion && (
          <p className="small muted">版を指定したテンプレートだけを、新しいサーバーの既定にできます。</p>
        )}
      </div>
    </Dialog>
  );
}

/** 別の版で複製する。その版で使えない Mod があれば、外して複製するかを聞く */
function DuplicateDialog({ source, close, done }: { source: ClientModTemplate; close: () => void; done: () => void }) {
  const { notify } = useApp();
  const [name, setName] = useState(`${source.name}（コピー）`);
  const [mcVersion, setMcVersion] = useState('');
  const [versions, setVersions] = useState<string[]>();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void api
      .versions('fabric')
      .then((list) => {
        if (cancelled) return;
        const ids = list.filter((v) => v.availability !== 'unchecked').map((v) => v.id);
        setVersions(ids);
        setMcVersion((current) => current || ids.find((id) => id !== source.mcVersion) || '');
      })
      .catch(() => !cancelled && setVersions([]));
    return () => {
      cancelled = true;
    };
  }, [source.mcVersion]);
  const submit = async () => {
    setBusy(true);
    try {
      try {
        await clientModsApi.duplicate(source.id, { name, mcVersion });
      } catch (error) {
        // その版で使えない Mod があるときは、外して複製してよいかを確かめる
        if (!(error instanceof ApiError && error.status === 409)) throw error;
        if (!window.confirm(`${errorMessage(error)}\n\nこれらの Mod を外して複製しますか？`)) return;
        await clientModsApi.duplicate(source.id, { name, mcVersion, dropUnavailable: true });
      }
      notify(`「${name}」を作成しました`);
      done();
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={`「${source.name}」を別の版で複製`}
      onClose={close}
      actions={
        <>
          <button className="btn" onClick={close}>
            キャンセル
          </button>
          <button className="btn primary" disabled={busy || !name.trim() || !mcVersion} onClick={() => void submit()}>
            {busy && <Spinner />}
            複製
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label htmlFor="client-duplicate-name">名前</label>
          <input
            id="client-duplicate-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="client-duplicate-mc">Minecraft の版</label>
          {versions && versions.length === 0 ? (
            <input
              id="client-duplicate-mc"
              className="input"
              placeholder="版の一覧を取れませんでした。例: 1.21.1"
              value={mcVersion}
              onChange={(event) => setMcVersion(event.target.value)}
            />
          ) : (
            <select
              id="client-duplicate-mc"
              className="input"
              value={mcVersion}
              disabled={!versions}
              onChange={(event) => setMcVersion(event.target.value)}
            >
              {!versions && <option value="">読み込み中…</option>}
              {versions?.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          )}
        </div>
        <p className="small muted">
          Mod は同じものを入れ、配るときにこの版の Fabric 用の配布ファイルを選びます。この版で使えない Mod
          があれば、複製の前にお知らせします。
        </p>
      </div>
    </Dialog>
  );
}

export function ClientModTemplatesSection() {
  const { canOperate, notify } = useApp();
  const templates = usePoll(() => clientModsApi.list(), 60000);
  const [creating, setCreating] = useState(false);
  const [duplicating, setDuplicating] = useState<ClientModTemplate>();
  const [assigning, setAssigning] = useState<ClientModTemplate>();
  const [filter, setFilter] = useState('');
  const [params, setParams] = useSearchParams();
  const editing = creating ? 'new' : templates.data?.find((entry) => entry.id === params.get('clientTemplate'));
  const open = (id?: string) => {
    setCreating(!id);
    const next = new URLSearchParams(params);
    next.delete('server');
    next.set('clientTab', 'mods');
    if (id) next.set('clientTemplate', id);
    else next.delete('clientTemplate');
    setParams(next);
  };
  const close = () => {
    setCreating(false);
    const next = new URLSearchParams(params);
    for (const key of ['clientTemplate', 'clientTab', 'server']) next.delete(key);
    setParams(next);
  };
  const remove = async (template: ClientModTemplate) => {
    if (!window.confirm(`「${template.name}」を削除しますか？選んでいたサーバーは「なし」に戻ります。`)) return;
    try {
      await clientModsApi.delete(template.id);
      await templates.reload();
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };
  const list = templates.data ?? [];
  const templateList = useAnimatedList(list, (template) => template.id);
  const versions = [...new Set(list.map(versionLabel))].sort((a, b) =>
    b.localeCompare(a, undefined, { numeric: true }),
  );
  const shown = templateList.items.filter((template) => !filter || versionLabel(template) === filter);
  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h2 data-search-id="client-templates">クライアント Mod テンプレート</h2>
          <p>
            Mod なしのサーバーに参加するプレイヤーに配る、軽量化など、プレイヤー側だけで動く Mod
            の組み合わせです。Minecraft の版ごとに作ります。
          </p>
          <p className="small muted">
            どのテンプレートを使うかは、サーバーごとに「拡張」→「ランチャー」で選びます。選べるのはサーバーと同じ版のテンプレートだけです。
          </p>
        </div>
        {canOperate && (
          <button className="btn primary" onClick={() => open()}>
            クライアント Mod テンプレートを作成
          </button>
        )}
      </div>
      <ErrorAlert error={templates.error} />
      {list.length > 0 && (
        <div className="field" style={{ maxWidth: 240 }}>
          <label htmlFor="client-template-filter">Minecraft の版で絞り込む</label>
          <select
            id="client-template-filter"
            className="input"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          >
            <option value="">すべての版</option>
            {versions.map((version) => (
              <option key={version} value={version}>
                {version}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="card">
        <LoadingContent loading={!templates.data && templates.loading}>
          {templates.data && shown.length === 0 ? (
            <Empty>
              {list.length === 0
                ? 'クライアント Mod テンプレートはまだありません。'
                : 'この版のテンプレートはありません。'}
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>名前</th>
                    <th>Minecraft の版</th>
                    <th>Mod の数</th>
                    <th>使っているサーバー</th>
                    <th>起動テスト</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((template) => (
                    <tr
                      key={template.id}
                      ref={templateList.ref}
                      className={templateList.className(template)}
                      inert={!list.some((item) => item.id === template.id)}
                    >
                      <td>
                        <button className="btn ghost small" onClick={() => open(template.id)}>
                          {template.name}
                        </button>
                        {template.defaultForNew && <span className="tag">新しいサーバーの既定</span>}
                      </td>
                      <td className="mono">{versionLabel(template)}</td>
                      <td>{template.mods.length}</td>
                      <td className="small">
                        {template.servers?.length
                          ? template.servers.map((server, index) => (
                              <span key={server.id}>
                                {index > 0 && '、'}
                                <Link to={`/servers/${encodeURIComponent(server.id)}`}>{server.name}</Link>
                              </span>
                            ))
                          : 'なし'}
                        {canOperate && (
                          <button className="btn ghost small" onClick={() => setAssigning(template)}>
                            割り当て
                          </button>
                        )}
                      </td>
                      <td>
                        <ClientTemplateVerifications templateId={template.id} compact />
                      </td>
                      <td>
                        <div className="row">
                          <button className="btn small" onClick={() => open(template.id)}>
                            {canOperate ? '編集' : '開く'}
                          </button>
                          {canOperate && (
                            <>
                              <button className="btn small" onClick={() => setDuplicating(template)}>
                                別の版で複製
                              </button>
                              <button className="btn ghost small danger" onClick={() => void remove(template)}>
                                削除
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </LoadingContent>
      </div>
      {editing && (
        <ClientTemplateEditor
          key={editing === 'new' ? 'new' : editing.id}
          initial={editing === 'new' ? undefined : editing}
          close={close}
          saved={() => {
            if (editing === 'new') close();
            void templates.reload();
          }}
        />
      )}
      {assigning && (
        <AssignDialog
          template={assigning}
          close={() => setAssigning(undefined)}
          done={() => {
            setAssigning(undefined);
            void templates.reload();
          }}
        />
      )}
      {duplicating && (
        <DuplicateDialog
          source={duplicating}
          close={() => setDuplicating(undefined)}
          done={() => {
            setDuplicating(undefined);
            void templates.reload();
          }}
        />
      )}
    </section>
  );
}
